import React from 'react';
import classNames from 'classnames';
import { DotIcon } from '@/design-system';
import Link from 'next/link';

export interface SidebarMenuItemProps {
  className?: string;
  active?: boolean;
  children: React.ReactNode;
  icon: React.FC<{ className?: string }>;
  href: string;
  target?: '_self' | '_blank';
}

export const SidebarMenuItem = ({
  icon: MenuIcon,
  children,
  className,
  active,
  href = '',
  target = '_self',
}: SidebarMenuItemProps) => {
  const containerClasses = classNames(
    'relative py-2 pr-3 pl-3',
    'flex gap-4 items-center rounded-ds-button-s group/sidebar-item hover:cursor-pointer transition-colors duration-200 ease-in-out',
    '[.active]:pl-5 [.active]:transition-[padding,color,background-color] [.active]:duration-500 [.active]:ease-in-out',
    'group-[.collapsed]/sidebar:[.active]:pl-3!',
    'not-[.active]:hover:bg-ds-bg-card-hover',
    '[.active]:bg-ds-bg-card-active',
    {
      active,
    },
    className,
  );

  const dotIconClasses = classNames(
    'fill-ds-icon-2',
    'absolute top-1/2 left-[10px] -translate-x-1/2 -translate-y-1/2',
    'w-5 h-5 group-[.collapsed]/sidebar:w-4 group-[.collapsed]/sidebar:h-4 transition-all duration-500 ease-in-out',
    'opacity-0 [.active]:opacity-100',
    { active },
  );

  const iconClasses = classNames(
    'fill-ds-icon-4 text-ds-icon-4',
    'group-hover/sidebar-item:fill-ds-icon-inverse-4 group-hover/sidebar-item:text-ds-icon-inverse-4',
    'group-hover/sidebar-item:stroke-ds-icon-inverse-4 group-hover/sidebar-item:[stroke-width:0.5px]',
    '[.active]:fill-ds-icon-inverse-3 [.active]:text-ds-icon-inverse-3',
    '[.active]:stroke-ds-icon-inverse-3 [.active]:[stroke-width:0.5px]',
    {
      active,
    },
  );

  const textClasses = classNames(
    'font-ds-400',
    'group-hover/sidebar-item:text-ds-text-inverse-4',
    'group-hover/sidebar-item:font-ds-600',
    '[.active]:text-ds-text-inverse-1',
    '[.active]:font-ds-600',
    {
      active,
    },
  );

  const isExternal = href.startsWith('http');
  const Tag = isExternal ? 'a' : Link;
  const linkProps = isExternal ? { href } : { href, prefetch: true };

  return (
    <li>
      <Tag {...linkProps} className={containerClasses} target={target}>
        <div className="flex shrink-0">
          <DotIcon className={dotIconClasses} />
          <MenuIcon className={classNames(iconClasses, 'size-6 shrink-0')} />
        </div>
        <span className={textClasses}>{children}</span>
      </Tag>
    </li>
  );
};
